import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { usePlacesAutocomplete, type PlaceSuggestion } from '../hooks/usePlacesAutocomplete';
import './LocationAutocompleteField.css';

interface LocationAutocompleteFieldProps {
  value: string;
  /** Free-text typing — the caller should clear any stored placeId. */
  onChange: (text: string) => void;
  /** A suggestion was picked — a resolvable place with its Google placeId. */
  onSelect: (place: { fullText: string; placeId: string }) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

/**
 * A controlled location input with Google Places autocomplete. The suggestions
 * dropdown is rendered inline (absolutely positioned under the field), so it
 * stays part of the surrounding modal instead of floating elsewhere. Typing
 * offers real-place suggestions; selecting one fills the field with the resolved
 * text and reports the placeId. Free text is still allowed (no placeId).
 */
export function LocationAutocompleteField({
  value,
  onChange,
  onSelect,
  placeholder = 'Add location',
  className = '',
  id,
}: LocationAutocompleteFieldProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  // After a selection, the input holds the chosen place — gate the hook so it
  // doesn't fire a redundant (billable) autocomplete request for that value.
  const [justSelected, setJustSelected] = useState(false);
  const { suggestions } = usePlacesAutocomplete(value, { enabled: !justSelected });

  const showDropdown = open && !justSelected && suggestions.length > 0;

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setActiveIndex(-1);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const select = (s: PlaceSuggestion) => {
    setJustSelected(true);
    onSelect({ fullText: s.fullText || s.primaryText, placeId: s.placeId });
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : i));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : i));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        select(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className={`location-autocomplete ${className}`}>
      <input
        id={id}
        type="text"
        className="event-field-input"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          setJustSelected(false);
          setOpen(true);
          onChange(e.target.value);
        }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && (
        <div className="location-autocomplete-dropdown">
          {suggestions.map((s, idx) => (
            <button
              type="button"
              key={s.id || s.placeId}
              className={`location-autocomplete-option${idx === activeIndex ? ' is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); select(s); }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className="location-autocomplete-primary">{s.primaryText}</span>
              {s.secondaryText && <span className="location-autocomplete-secondary">{s.secondaryText}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
